import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PuzzleAccessGate from "../../login/PuzzleAccessGate";
import { SITE_BG } from "../../../dark-theme/palette";
import HomeCreditService from "../../../services/HomeCreditService";
import HomeService from "../../../services/HomeService";
import Logger from "../../../services/Logger";
import UserService from "../../../services/UserService";
import UtilService from "../../../services/UtilService";
import SubscriptionDialog from "../../subscription/SubscriptionDialog";
import { usePagesTranslation } from "../../locales";
import { UserLayoutContext } from "../../route/UserLayoutContext";
import SlidingPuzzlePlay from "./SlidingPuzzlePlay";
import usePlayRecord from "../common/hooks/usePlayRecord";
import { trackEvent } from "../../../services/GoogleAnalytics";
import IconBack from "../../components/IconBack";
import {
    buildPuzzleAccessContext,
    getSlidingGridLockReason,
    isSlidingPremiumTier,
} from "../common/puzzleTierAccess";
import {
    getSlidingStoryTitle,
    pickSlidingPlayImageUrl,
    pickSlidingToolbarPreviewImageUrl,
} from "../../browse-sliding/slidingBrowseUtils";
import PlayBoardSeoHead from "../../components/PlayBoardSeoHead";
import { saveSlidingPrefsFromTemplate } from "../common/puzzlePlayPrefs";

/** Story picker shell under `UserLayout` site header. */
const BELOW_APP_HEADER_MIN = "calc(100vh - 5.5rem)";
/** Board play: full viewport — site header hidden on board route. */
/** Fill parent — site header hidden on board route. Avoid 100vh (OBS CEF misreports vh). */
const BOARD_PLAY_VIEWPORT_HEIGHT = "100%";

/** Same icon sizing as crossword play toolbar (`CrossPlayMain`). */
const PLAY_TOOLBAR_ICON_BTN_SIZING_SX = {
    padding: "5px",
    "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
};

export default function SlidingPuzzlePlayPage() {
    const { storyId, slidingId } = useParams();
    const navigate = useNavigate();

    usePlayRecord({
        puzzleType: "SLIDING",
        puzzleId: slidingId,
        enabled: Boolean(slidingId),
    });

    useEffect(() => {
        trackEvent("entered", {
            name: "SlidingPuzzlePlayPage",
            puzzle_type: "SLIDING",
            page_kind: "play",
            story_id: storyId ?? undefined,
            puzzle_id: slidingId ?? undefined,
        });
    }, [storyId, slidingId]);

    const location = useLocation();
    const { t } = usePagesTranslation();
    const { onPuzzleTitleChange } = useContext(UserLayoutContext);

    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [userInfo, setUserInfo] = useState(null);

    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [userSubscription, setUserSubscription] = useState("free");
    const [subscriptionTriggerReason] = useState("general");
    /** null | pending | ok | insufficient | error — premium board credit charge */
    const [premiumCreditStatus, setPremiumCreditStatus] = useState(null);
    const [premiumCreditError, setPremiumCreditError] = useState("");

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setGroup(null);
            try {
                const [data, userData] = await Promise.all([
                    storyId ? HomeService.getSlidingStoryGroup(storyId) : Promise.resolve(null),
                    UserService.getUserInfo().catch(() => null),
                ]);
                if (cancelled) return;
                setGroup(data);
                setUserInfo(userData);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        if (storyId) load();
        else setLoading(false);

        return () => {
            cancelled = true;
        };
    }, [storyId]);

    const allTemplates = group?.templates ?? [];
    const templates = slidingId
        ? allTemplates.filter((t) => t.id === slidingId)
        : allTemplates;
    const story = group?.story ?? null;
    const playImageUrl = useMemo(() => pickSlidingPlayImageUrl(story), [story]);
    const toolbarPreviewImageUrl = useMemo(() => pickSlidingToolbarPreviewImageUrl(story), [story]);

    const hasTemplates = templates.length > 0;
    const canShowPlay = Boolean(group && hasTemplates && playImageUrl);
    const isAuthenticated = Boolean(userInfo?.cloudUser);
    const userPayPlan = UtilService.resolveWebPayPlan(userInfo?.cloudUser);

    const activeTemplate = useMemo(() => {
        if (!slidingId) return null;
        return allTemplates.find((t) => t.id === slidingId) ?? null;
    }, [allTemplates, slidingId]);

    const accessContext = useMemo(() => buildPuzzleAccessContext(userInfo), [userInfo]);

    const isFreePuzzle = group?.storySliding?.free === true;
    const boardLockReason = useMemo(() => {
        if (!slidingId || !activeTemplate) return null;
        const gridSize = activeTemplate.gridSize ?? activeTemplate.grid_size;
        const difficultyLevel =
            activeTemplate.difficultyLevel ?? activeTemplate.difficulty_level;
        return getSlidingGridLockReason(gridSize, accessContext, difficultyLevel, { isFreePuzzle });
    }, [slidingId, activeTemplate, accessContext, isFreePuzzle]);

    const needsPremiumCreditCharge = useMemo(() => {
        if (!slidingId || !activeTemplate || boardLockReason) return false;
        if (accessContext.isStaff || isFreePuzzle) return false;
        const gridSize = activeTemplate.gridSize ?? activeTemplate.grid_size;
        const difficultyLevel =
            activeTemplate.difficultyLevel ?? activeTemplate.difficulty_level;
        return isSlidingPremiumTier(gridSize, difficultyLevel);
    }, [slidingId, activeTemplate, boardLockReason, accessContext.isStaff, isFreePuzzle]);

    useEffect(() => {
        let cancelled = false;

        async function chargePremiumCredit() {
            if (!needsPremiumCreditCharge || !slidingId) {
                setPremiumCreditStatus(needsPremiumCreditCharge ? null : "ok");
                setPremiumCreditError("");
                return;
            }
            setPremiumCreditStatus("pending");
            setPremiumCreditError("");
            try {
                const result = await HomeCreditService.consumeSlidingPremiumPlay(slidingId);
                if (cancelled) return;
                setPremiumCreditStatus("ok");
                if (result.creditsConsumed > 0) {
                    trackEvent("sliding_premium_credit_consumed", {
                        puzzle_id: slidingId,
                        credits_consumed: result.creditsConsumed,
                        credit_balance: result.creditBalance,
                    });
                }
            } catch (err) {
                if (cancelled) return;
                const message = err?.message || "";
                Logger.warn("Sliding premium credit consume failed:", message);
                if (/insufficient/i.test(message)) {
                    setPremiumCreditStatus("insufficient");
                    setSubscriptionModalOpen(true);
                } else {
                    setPremiumCreditStatus("error");
                    setPremiumCreditError(message);
                }
            }
        }

        chargePremiumCredit();
        return () => {
            cancelled = true;
        };
    }, [needsPremiumCreditCharge, slidingId]);

    useEffect(() => {
        const userId = userInfo?.cloudUser?.id;
        if (!slidingId || !userId || !activeTemplate) return;
        saveSlidingPrefsFromTemplate(userId, activeTemplate);
    }, [slidingId, userInfo?.cloudUser?.id, activeTemplate]);

    const showAccessGate = canShowPlay && Boolean(boardLockReason);

    const headerTitle = useMemo(() => {
        if (!canShowPlay || showAccessGate) return "";
        const tt = story ? getSlidingStoryTitle(story) : "";
        return tt || t("puzzles-browse.sliding-play-puzzle-heading");
    }, [canShowPlay, showAccessGate, story, t]);

    useEffect(() => {
        if (!onPuzzleTitleChange || slidingId) return;
        if (loading) {
            onPuzzleTitleChange(null);
            return;
        }
        if (headerTitle) {
            onPuzzleTitleChange(headerTitle);
        } else {
            onPuzzleTitleChange(null);
        }
    }, [loading, headerTitle, onPuzzleTitleChange, slidingId]);

    const handleBackClick = () => {
        if (location.state?.fromLibrary === true) {
            navigate("/account/library", {
                state: { activeTab: location.state?.activeTab ?? 0 },
            });
            return;
        }
        navigate(`/puzzle/play-sliding/${storyId}`, { state: location.state });
    };

    const handleSubscribe = () => {
        setUserSubscription("premium");
        setSubscriptionModalOpen(false);
    };

    const backButton = (
        <IconBack
            onClick={handleBackClick}
            sx={{ ...PLAY_TOOLBAR_ICON_BTN_SIZING_SX }}
        />
    );

    const shellBeforePlay = (
        <>
            <Box sx={{ mb: 2 }}>{backButton}</Box>
        </>
    );

    const boardPlayShellSx = slidingId
        ? {
              p: { xs: 0.75, sm: 1 },
              maxWidth: "none",
              width: "100%",
              height: BOARD_PLAY_VIEWPORT_HEIGHT,
              maxHeight: BOARD_PLAY_VIEWPORT_HEIGHT,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              position: "relative",
              bgcolor: SITE_BG,
          }
        : {};

    const playBoardSeoNoindex =
        Boolean(slidingId) && !loading && (!group || !activeTemplate);

    const playBoardSeoHead = slidingId ? (
        <PlayBoardSeoHead
            kind="sliding"
            storyId={storyId}
            boardId={slidingId}
            story={story}
            board={activeTemplate}
            noindex={playBoardSeoNoindex}
        />
    ) : null;

    const withPlayBoardSeo = (node) => (
        <>
            {playBoardSeoHead}
            {node}
        </>
    );

    if (loading) {
        return withPlayBoardSeo(
            <Box
                sx={{
                    p: { xs: 2, sm: 3 },
                    maxWidth: slidingId ? "none" : 1400,
                    mx: slidingId ? 0 : "auto",
                    width: slidingId ? "100%" : undefined,
                    minHeight: slidingId ? BOARD_PLAY_VIEWPORT_HEIGHT : BELOW_APP_HEADER_MIN,
                    display: "flex",
                    flexDirection: "column",
                    ...boardPlayShellSx,
                }}
            >
                {shellBeforePlay}
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 0,
                        py: 4,
                    }}
                >
                    <CircularProgress />
                </Box>
            </Box>,
        );
    }

    if (!storyId) {
        return withPlayBoardSeo(
            <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: "auto" }}>
                {shellBeforePlay}
                <Typography color="error">{t("puzzles-browse.detail-not-found")}</Typography>
            </Box>,
        );
    }

    if (!group) {
        return withPlayBoardSeo(
            <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: "auto" }}>
                {shellBeforePlay}
                <Typography color="text.secondary">{t("puzzles-browse.detail-not-found")}</Typography>
            </Box>,
        );
    }

    if (!canShowPlay) {
        return withPlayBoardSeo(
            <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: "auto" }}>
                {shellBeforePlay}
                <Typography color="text.secondary">{t("puzzles-browse.sliding-play-unavailable")}</Typography>
            </Box>,
        );
    }

    if (showAccessGate) {
        const gateTitle = story ? getSlidingStoryTitle(story) : headerTitle;
        return withPlayBoardSeo(
            <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: "auto", minHeight: BELOW_APP_HEADER_MIN }}>
                {shellBeforePlay}
                <PuzzleAccessGate
                    isAuthenticated={isAuthenticated}
                    userPayPlan={userPayPlan}
                    onUpgradeClick={() => setSubscriptionModalOpen(true)}
                    puzzleTitle={gateTitle}
                    onBackClick={handleBackClick}
                />
                <SubscriptionDialog
                    open={subscriptionModalOpen}
                    onClose={() => setSubscriptionModalOpen(false)}
                    onSubscribe={handleSubscribe}
                    triggerReason={subscriptionTriggerReason}
                    userSubscription={userSubscription}
                />
            </Box>,
        );
    }

    if (slidingId && needsPremiumCreditCharge && premiumCreditStatus !== "ok"
        && premiumCreditStatus !== "insufficient" && premiumCreditStatus !== "error") {
        return withPlayBoardSeo(
            <Box
                sx={{
                    p: { xs: 2, sm: 3 },
                    maxWidth: "none",
                    width: "100%",
                    minHeight: BOARD_PLAY_VIEWPORT_HEIGHT,
                    display: "flex",
                    flexDirection: "column",
                    ...boardPlayShellSx,
                }}
            >
                {shellBeforePlay}
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 0,
                        py: 4,
                    }}
                >
                    <CircularProgress />
                </Box>
            </Box>,
        );
    }

    if (
        slidingId
        && needsPremiumCreditCharge
        && (premiumCreditStatus === "insufficient" || premiumCreditStatus === "error")
    ) {
        const gateTitle = story ? getSlidingStoryTitle(story) : headerTitle;
        return withPlayBoardSeo(
            <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: "auto", minHeight: BELOW_APP_HEADER_MIN }}>
                {shellBeforePlay}
                <Alert severity={premiumCreditStatus === "insufficient" ? "warning" : "error"} sx={{ mb: 2 }}>
                    {premiumCreditStatus === "insufficient"
                        ? t("core.puzzleAccess.insufficientCredits")
                        : (premiumCreditError || t("core.puzzleAccess.creditChargeError"))}
                </Alert>
                {premiumCreditStatus === "insufficient" && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
                        {t("core.puzzleAccess.insufficientCreditsHint")}
                    </Typography>
                )}
                <PuzzleAccessGate
                    isAuthenticated={isAuthenticated}
                    userPayPlan={userPayPlan}
                    onUpgradeClick={() => setSubscriptionModalOpen(true)}
                    puzzleTitle={gateTitle}
                    onBackClick={handleBackClick}
                />
                <SubscriptionDialog
                    open={subscriptionModalOpen}
                    onClose={() => setSubscriptionModalOpen(false)}
                    onSubscribe={handleSubscribe}
                    triggerReason={subscriptionTriggerReason}
                    userSubscription={userSubscription}
                />
            </Box>,
        );
    }

    return withPlayBoardSeo(
        <Box
            sx={{
                p: { xs: 2, sm: 3 },
                maxWidth: slidingId ? "none" : 1400,
                mx: slidingId ? 0 : "auto",
                width: slidingId ? "100%" : undefined,
                ...(slidingId
                    ? boardPlayShellSx
                    : {
                          position: "relative",
                          isolation: "isolate",
                          minHeight: BELOW_APP_HEADER_MIN,
                          overflow: "hidden",
                          bgcolor: SITE_BG,
                          borderRadius: { xs: 0, sm: 2 },
                      }),
            }}
        >
            <SlidingPuzzlePlay
                key={slidingId ? `${storyId}-${slidingId}` : storyId}
                storyId={storyId}
                templates={templates}
                imageUrl={playImageUrl}
                previewImageUrl={toolbarPreviewImageUrl}
                toolbarLeading={backButton}
                cloudUserId={userInfo?.cloudUser?.id ?? undefined}
                isAuthenticated={isAuthenticated}
                hideSelectors={Boolean(slidingId)}
                sharePuzzleTitle={story ? getSlidingStoryTitle(story) : ""}
                soundEnabled={false}
                bgmAutoPlay={false}
            />
        </Box>,
    );
}
